/**
 * Where the intro's tick list comes from. Preferred: a baked `TITLE.TTM`
 * resource, decoded with `parseTtm` and executed by the real interpreter.
 * Fallback: the transcript module, which tests prove identical to that
 * interpreter's output for the real file.
 *
 * The choice is made off the bake manifest rather than by probing a URL, so
 * today's "no `.TTM` bake stage" costs no request and logs no 404 — the
 * pipeline currently files `TITLE.TTM` under `unbaked`, and the day it moves
 * to `entries` this picks it up with no code change.
 */

import type { BakeManifest } from "@red-opus/baker/schema";
import { parseTtm } from "@red-opus/formats";
import { type IntroTick, ticksFromScript } from "./intro-ticks.ts";
import { TITLE_TTM_TICKS } from "./title-ttm-transcript.ts";

export type IntroSourceKind = "baked-ttm" | "transcript";

export interface IntroSource {
  readonly kind: IntroSourceKind;
  readonly ticks: readonly IntroTick[];
}

const TITLE_SCRIPT = "TITLE.TTM";

/** The baked artifact this expects is the raw `.TTM` resource extract, which `parseTtm` reads directly. */
export function bakedTitleScriptPath(manifest: BakeManifest): string | undefined {
  const entry = manifest.entries.find(
    (candidate) => candidate.sourcePath.toUpperCase().endsWith(TITLE_SCRIPT) || candidate.id.toUpperCase().endsWith(TITLE_SCRIPT),
  );
  return entry ? `/baked/${entry.path}` : undefined;
}

export async function loadIntroSource(manifest: BakeManifest): Promise<IntroSource> {
  const path = bakedTitleScriptPath(manifest);
  if (path) {
    try {
      const response = await fetch(path);
      if (response.ok) {
        const script = parseTtm(new Uint8Array(await response.arrayBuffer()));
        if (script.instructions.length > 0) return { kind: "baked-ttm", ticks: ticksFromScript(script) };
      }
    } catch {
      // A baked artifact in a shape `parseTtm` can't read is a bake-stage problem, not a reason to lose the intro.
    }
  }
  return { kind: "transcript", ticks: TITLE_TTM_TICKS };
}
